import { hexToRgb, normalizeHex, previewHex, sanitizeHexInput } from '../math/color.js';
import { clamp01 } from '../math/scalar.js';
import { openColorPicker, type ColorPickerHandle } from './colorPicker.js';

export type EditableColorFieldClassNames = {
  field: string;
  swatchButton: string;
  swatch: string;
  valueInput: string;
};

export type ColorControlClassNames = {
  container: string;
  leading: string;
  trailing?: string;
  label?: string;
};

type EditableColorFieldOptions = {
  value: string;
  opacity?: number;
  classNames: EditableColorFieldClassNames;
  inputAriaLabel?: string;
  stopMouseDownPropagation?: boolean;
  onBegin?: () => void;
  onInputValue: (hex: string) => void;
  onOpacityInput?: (opacity: number) => void;
  onCommit?: () => void;
};

export interface EditableColorFieldElement extends HTMLDivElement {
  setHex: (hex: string) => void;
  setOpacity: (opacity: number) => void;
}

export function createEditableColorField(options: EditableColorFieldOptions): EditableColorFieldElement {
  const field = Object.assign(document.createElement('div'), {
    setHex: (_hex: string): void => {},
    setOpacity: (_opacity: number): void => {},
  });
  field.className = options.classNames.field;
  let currentHex = options.value.toUpperCase();
  let currentOpacity = typeof options.opacity === 'number' ? clamp01(options.opacity) : null;

  const swatchButton = document.createElement('button');
  swatchButton.type = 'button';
  swatchButton.className = options.classNames.swatchButton;
  swatchButton.setAttribute('aria-label', 'Open color picker');

  const swatch = document.createElement('span');
  swatch.className = options.classNames.swatch;
  swatch.style.background = currentHex;
  swatchButton.appendChild(swatch);
  field.appendChild(swatchButton);

  const valueInput = document.createElement('input');
  valueInput.className = options.classNames.valueInput;
  valueInput.type = 'text';
  valueInput.spellcheck = false;
  valueInput.value = currentHex;
  valueInput.setAttribute('aria-label', options.inputAriaLabel ?? 'Hex color');
  field.appendChild(valueInput);

  const maybeStopMouseDown = (event: MouseEvent): void => {
    if (options.stopMouseDownPropagation) {
      event.stopPropagation();
    }
  };

  let picker: ColorPickerHandle | null = null;

  const swatchColor = (): string => {
    if (currentOpacity === null) return currentHex;
    const rgb = hexToRgb(currentHex);
    const red = Math.round(rgb[0] * 255);
    const green = Math.round(rgb[1] * 255);
    const blue = Math.round(rgb[2] * 255);
    return `rgb(${red} ${green} ${blue} / ${currentOpacity})`;
  };

  const syncSwatch = (): void => {
    swatchButton.classList.toggle('is-transparent', currentOpacity !== null && currentOpacity < 1);
    swatch.style.background = swatchColor();
  };

  const applyHex = (next: string, syncPicker = true, emitChange = true): void => {
    currentHex = next.toUpperCase();
    syncSwatch();
    valueInput.value = currentHex;
    if (syncPicker) picker?.setHex(currentHex);
    if (emitChange) options.onInputValue(currentHex);
  };

  const applyOpacity = (next: number, syncPicker = true, emitChange = true): void => {
    currentOpacity = clamp01(next);
    syncSwatch();
    if (syncPicker) picker?.setOpacity(currentOpacity);
    if (emitChange) options.onOpacityInput?.(currentOpacity);
  };

  field.setHex = hex => {
    const normalized = normalizeHex(hex);
    if (normalized) applyHex(normalized, true, false);
  };
  field.setOpacity = value => applyOpacity(value, true, false);

  const togglePicker = (): void => {
    if (picker) {
      picker.close();
      return;
    }
    picker = openColorPicker({
      anchor: swatchButton,
      hex: currentHex,
      opacity: currentOpacity ?? undefined,
      onBegin: () => options.onBegin?.(),
      onInput: hex => applyHex(hex, false),
      onOpacityInput: options.onOpacityInput ? value => applyOpacity(value, false) : undefined,
      onCommit: () => options.onCommit?.(),
      onClose: () => {
        picker = null;
      },
    });
  };

  swatchButton.addEventListener('mousedown', maybeStopMouseDown);
  swatchButton.addEventListener('click', event => {
    event.preventDefault();
    togglePicker();
  });

  valueInput.addEventListener('focus', () => options.onBegin?.());
  valueInput.addEventListener('mousedown', event => {
    maybeStopMouseDown(event);
    if (event.detail < 2) return;
    event.preventDefault();
    valueInput.focus();
    valueInput.setSelectionRange(0, valueInput.value.length);
  });
  valueInput.addEventListener('input', () => {
    const sanitized = sanitizeHexInput(valueInput.value);
    if (valueInput.value !== sanitized) {
      const caret = sanitized.length;
      valueInput.value = sanitized;
      valueInput.setSelectionRange(caret, caret);
    }
    const preview = previewHex(sanitized);
    if (!preview) return;
    applyHex(preview);
    valueInput.value = sanitized;
  });

  const commitHex = (): void => {
    const normalized = normalizeHex(valueInput.value) ?? previewHex(valueInput.value) ?? currentHex;
    applyHex(normalized);
    options.onCommit?.();
  };

  valueInput.addEventListener('change', commitHex);
  valueInput.addEventListener('blur', commitHex);

  syncSwatch();

  return field;
}

export function createColorControl(options: {
  classNames: ColorControlClassNames;
  colorField: HTMLElement;
  trailingControl?: HTMLElement;
  labelText?: string;
}): HTMLDivElement {
  const control = document.createElement('div');
  control.className = options.classNames.container;

  const leading = document.createElement('div');
  leading.className = options.classNames.leading;

  if (options.labelText && options.classNames.label) {
    const label = document.createElement('span');
    label.className = options.classNames.label;
    label.textContent = options.labelText;
    leading.appendChild(label);
  }

  leading.appendChild(options.colorField);
  control.appendChild(leading);

  if (options.trailingControl) {
    const trailing = document.createElement('div');
    if (options.classNames.trailing) {
      trailing.className = options.classNames.trailing;
    }
    trailing.appendChild(options.trailingControl);
    control.appendChild(trailing);
  }

  return control;
}
